import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fieldLabel } from "./DiaryTrendChart";
import { DiaryQuestions } from "./DiaryQuestions";
import { DiaryAddFlag } from "./DiaryAddFlag";

const WORDING = [
  ["intro", "Introduction at the top of the diary page", true],
  ["copy.invite_body", "Invitation email — main line", false],
  ["copy.invite_note", "Invitation email — extra note", true],
  ["copy.not_monitored", "“Not monitored continuously” notice", true],
  ["copy.safety_notice", "When to stop and get help", true],
  ["copy.done_message", "Thank-you message after the diary is sent", true],
  ["copy.disclaimer", "Footer disclaimer", false],
];
const STAT = { max: "highest", min: "lowest", mean: "average", answered: "number answered", missing: "number left blank",
  days_with: "number of days with", count: "number of entries with" };
const OP = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=" };

const get = (c, path) => path.split(".").reduce((o, k) => o?.[k], c);
function put(c, path, v) {
  const next = structuredClone(c);
  const keys = path.split(".");
  let o = next;
  keys.slice(0, -1).forEach((k) => { o[k] = o[k] ?? {}; o = o[k]; });
  o[keys[keys.length - 1]] = v;
  return next;
}

const Section = ({ title, hint, children, testId }) => (
  <section className="space-y-3 rounded-lg border border-zinc-200 p-3" data-testid={testId}>
    <div><h4 className="font-display text-sm font-semibold text-zinc-950">{title}</h4>{hint && <p className="text-xs text-zinc-500">{hint}</p>}</div>
    {children}
  </section>
);

function RuleRow({ rule, i, fields, readOnly, onChange, onRemove }) {
  const w = rule.when || {};
  const f = fields.find((x) => x.code === w.field);
  const opt = f?.options?.find((o) => o.value === w.option);
  const max = ["days_with"].includes(w.stat) ? undefined : f?.type === "rating" ? 10 : undefined;
  return (
    <div className="space-y-2 rounded-md bg-zinc-50 p-2.5" data-testid={`diary-rule-${rule.code}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-800">
        <span>Flag when the <b>{STAT[w.stat] || w.stat}</b>{opt && <> “{opt.label}”</>} — <b>{f ? fieldLabel(f) : w.field}</b> is {OP[w.op] || w.op}</span>
        <Input type="number" min={0} max={max} step={w.stat === "mean" ? "0.5" : "1"} className="h-8 w-20 font-mono-num" readOnly={readOnly} data-testid={`diary-rule-${rule.code}-value`}
               value={w.value ?? ""} onChange={(e) => onChange(`diary.alert_rules.${i}.when.value`, e.target.value === "" ? "" : Number(e.target.value))} />
        {!readOnly && (
          <Button size="icon" variant="ghost" className="ml-auto h-7 w-7 text-red-600 hover:text-red-700" title="Remove flag"
                  onClick={() => window.confirm(`Remove the flag “${rule.label}” from this draft?`) && onRemove(i)} data-testid={`diary-rule-${rule.code}-remove`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div><Label className="text-xs">Flag wording staff see (update it if you change the number)</Label>
        <Input className="mt-1 h-8" value={rule.label || ""} readOnly={readOnly} data-testid={`diary-rule-${rule.code}-label`}
               onChange={(e) => onChange(`diary.alert_rules.${i}.label`, e.target.value)} /></div>
    </div>
  );
}

/** Form editor for a diary draft: client wording, question wording and flag levels (JSON stays available). */
export function DiaryDraftEditor({ content, onChange, readOnly }) {
  const [addingFlag, setAddingFlag] = useState(false);
  const change = (path, v) => onChange(put(content, path, v));
  const d = content.diary || {};
  const rules = d.alert_rules || [];
  const ruleFields = [...(d.entry_fields || []), ...(d.followup_fields || [])];
  return (
    <div className="space-y-4" data-testid="diary-draft-editor">
      <Section title="Client wording" hint="What the client reads on the diary page and in the invitation email." testId="diary-editor-wording">
        {WORDING.map(([path, label, long]) => (
          <div key={path}><Label className="text-xs">{label}</Label>
            {long
              ? <Textarea rows={3} className="mt-1 text-sm" value={get(content, path) || ""} readOnly={readOnly} onChange={(e) => change(path, e.target.value)} data-testid={`diary-editor-${path.replace(".", "-")}`} />
              : <Input className="mt-1" value={get(content, path) || ""} readOnly={readOnly} onChange={(e) => change(path, e.target.value)} data-testid={`diary-editor-${path.replace(".", "-")}`} />}
          </div>
        ))}
      </Section>
      <Section title="Flag levels" hint="When the practitioner is flagged. Add a flag for any 0–10 scale, number or choice question. Flags are reviewed at appointments — the diary isn't monitored continuously." testId="diary-editor-rules">
        {rules.map((r, i) => <RuleRow key={r.code} rule={r} i={i} fields={ruleFields} readOnly={readOnly} onChange={change}
                                      onRemove={(k) => change("diary.alert_rules", rules.filter((_, j) => j !== k))} />)}
        {!rules.length && <p className="text-xs text-zinc-400">No flags — this diary never alerts the practitioner.</p>}
        {!readOnly && (addingFlag
          ? <DiaryAddFlag fields={ruleFields} rules={rules} onCancel={() => setAddingFlag(false)}
                          onAdd={(r) => { change("diary.alert_rules", [...rules, r]); setAddingFlag(false); }} />
          : <Button size="sm" variant="outline" onClick={() => setAddingFlag(true)} data-testid="diary-add-flag-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add flag</Button>)}
      </Section>
      <Section title="Questions" hint="Add, reword, reorder or remove questions in this draft. An existing question's answer type stays fixed so results remain comparable; earlier diaries keep the version they used. 0–10 scale questions in “Each activity” appear in the trend chart, PDF and week-to-week comparison." testId="diary-editor-questions">
        <DiaryQuestions d={d} readOnly={readOnly} setDiary={(next) => change("diary", next)} />
      </Section>
    </div>
  );
}
